// Supported-locale registry for the public marketing site.
//
// EXTENSION POINT — to add a language for the global service:
//   1. add its code to the `LocaleCode` union below,
//   2. add one entry to `SUPPORTED_LOCALES`,
//   3. add `dictionaries/<code>.ts` (implements `MktDict`) and register it in `./index.ts`.
// No component code changes are required — the nav switcher and detection are registry-driven.

export type LocaleCode = 'en' | 'ko' | 'ja' | 'zh' | 'zh-TW'

export interface LocaleMeta {
  code: LocaleCode
  /** English name, for accessibility / tooling. */
  label: string
  /** Endonym shown in the language switcher (e.g. "한국어"). */
  nativeLabel: string
  dir: 'ltr' | 'rtl'
}

export const SUPPORTED_LOCALES: readonly LocaleMeta[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', dir: 'ltr' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', dir: 'ltr' },
  { code: 'zh', label: 'Chinese (Simplified)', nativeLabel: '简体中文', dir: 'ltr' },
  { code: 'zh-TW', label: 'Chinese (Traditional)', nativeLabel: '繁體中文', dir: 'ltr' },
]

export const DEFAULT_LOCALE: LocaleCode = 'en'

export const LOCALE_CODES: readonly LocaleCode[] = SUPPORTED_LOCALES.map((l) => l.code)

export function isLocaleCode(value: string | null | undefined): value is LocaleCode {
  return value != null && (LOCALE_CODES as readonly string[]).includes(value)
}

/** Maps a BCP-47 tag (e.g. "ko-KR", "en-US") to a supported locale, or null. */
export function matchLocale(tag: string | null | undefined): LocaleCode | null {
  if (!tag) return null
  const lower = tag.toLowerCase()
  // Exact tag match (case-insensitive), e.g. "zh-TW" -> "zh-TW".
  const exact = LOCALE_CODES.find((c) => c.toLowerCase() === lower)
  if (exact) return exact
  // Traditional Chinese scripts/regions -> zh-TW (else they'd fall back to zh).
  if (/^zh-(hant|tw|hk|mo)\b/.test(lower) && isLocaleCode('zh-TW')) return 'zh-TW'
  // Primary-subtag fallback, e.g. "zh-CN" -> "zh", "ko-KR" -> "ko".
  const primary = lower.split('-')[0]
  return isLocaleCode(primary) ? primary : null
}
